import { isRole, ROLE_PERMISSIONS, type Permission } from '@maestro/shared/permissions';
import { DEV_AUTH_BYPASS } from './auth-client';
import { authTokenCache } from './auth-token';

// The client half of the console's permission gate. Lives in lib/, not admin/, because
// the console's routes are lazily chunked: importing it from the console's 1k-line data
// layer would drag that module — and its CSV-parsing graph — into every one of those
// chunks.
//
// This is UX ONLY. It reads an UNVERIFIED JWT payload to spare someone a control that
// would 403; the BFF verifies the signature and is the real gate.

// Read the `role` claim from a JWT payload without verifying it. base64url → JSON;
// undefined on any malformed token. `atob` decodes latin1, which is fine for the ASCII
// `role` claim but would mojibake a non-ASCII one — don't reuse this for `email`/`name`.
export function jwtRole(token: string): string | undefined {
  const part = token.split('.')[1];
  if (!part) return undefined;
  try {
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as {
      role?: unknown;
    };
    return typeof json.role === 'string' ? json.role : undefined;
  } catch {
    return undefined;
  }
}

const DEV_ROLE_KEY = 'maestro.dev.role';
// Empty reads as unset: clearing the value and removing the key are the same intent,
// and one of them would otherwise lock the console with no way back but devtools.
function devRole(): string {
  try {
    return localStorage.getItem(DEV_ROLE_KEY) || 'admin';
  } catch {
    return 'admin';
  }
}

// undefined means UNDETERMINED — no token at all — and is the only case that fails open
// below. A token carrying no readable role is determined, so it resolves to a string
// matching no role instead.
//
// A real token wins over the dev override: a dev build against a reachable auth has a
// real session, and overriding it is how you end up testing a role nobody has.
export async function currentRole(): Promise<string | undefined> {
  const token = await authTokenCache.get();
  if (token != null) return jwtRole(token) ?? '';
  return DEV_AUTH_BYPASS ? devRole() : undefined;
}

// What the console should render for a role. An UNDETERMINED role gets everything: a
// failed token mint latches a 10s cooldown of nulls, so hiding on it would strip a real
// admin's console for 10s with no explanation, and would empty it entirely wherever
// auth is unconfigured — which the BFF itself still serves.
//
// This decides what renders. It is deliberately more generous than the permissions the
// BFF will actually honour, so it must never be used to authorize.
export function renderablePermissions(role: string | undefined): readonly Permission[] {
  if (role === undefined) return ROLE_PERMISSIONS.admin;
  return isRole(role) ? ROLE_PERMISSIONS[role] : [];
}

// Resolved ONCE per page: asking twice can straddle that cooldown and admit someone on
// an undetermined read, then render them a page built from a determined role.
//
// `permission` is what the page actually needs. It defaults to the console's own
// `console:view`, and pages holding something narrower pass it — this only decides
// whether to render, so an undetermined role still gets through to be refused by the BFF
// rather than shown a blank console for ten seconds.
export async function consoleAccess(
  permission: Permission = 'console:view',
): Promise<{ allowed: boolean; role: string | undefined }> {
  const role = await currentRole();
  return { allowed: renderablePermissions(role).includes(permission), role };
}
